/**
 * What a failure MEANS, expressed as the action it calls for.
 *
 * This is the file the slice exists for. The service deliberately distinguishes 401 from
 * 503 — `core/api/auth.py` says so in its own docstring — because a caller *retries* a 503
 * and *re-authenticates* on a 401, and answering 401 during an identity-provider outage
 * sends every client in the estate back to an IdP that is already down. A shell that
 * collapsed the two would be the thing that turns one provider hiccup into a login storm.
 *
 * The codes are the server's, and they are stable: `core/api/auth.py` and
 * `core/api/deps.py` both put `{"detail": {"code": …, "message": …}}` on the wire, and the
 * PROJECT.md follow-up that introduced them says the TypeScript client keys on the code
 * rather than on prose. Text matching survives only as a fallback for a service older
 * than the codes.
 *
 * The interesting case is the third one. **A 401 whose code is `unauthenticated`, when we
 * definitely sent a token, means the token was never looked at.** `build_verifier()`
 * returns None when neither INVENTORY_AUTH_JWKS_URL nor the dev hatch is configured, no
 * middleware is installed, nothing sets `request.state.claims`, and `require_tenant`
 * refuses everything from `resolve_tenant`'s `UNAUTHENTICATED` branch — which is the same
 * branch an anonymous request takes. A bad credential comes back as `invalid_token` from
 * the middleware instead. So the pair (token sent, code `unauthenticated`) is the exact
 * signature of a service that is not configured to authenticate, and it is the one 401
 * that must NOT be answered by re-authenticating: there is no token in the world that
 * would satisfy it.
 */

import { FeatureNotInPlanError, NetworkError, ServiceUnavailableError, UnauthorizedError, errorCode, getErrorMessage } from '@inai/inventory-core';

/**
 * Re-exported from the core, which now provides it.
 *
 * The shell carried a private copy of these six lines because `@inai/inventory-core` read
 * `detail.code` internally in `toApiError` and exposed no way to get at it. It exports
 * `errorCode` now, so the copy is gone and there is one implementation again.
 */
export { errorCode };

/**
 * The message the SERVER actually sent, when there is one.
 *
 * This began as a workaround: the core rewrote EVERY `FeatureNotInPlanError` as "Your plan
 * does not include this capability", and mapped a `tenant_claim_missing` 403 onto that
 * class — so a token not bound to an organization was answered with a billing message.
 * Observed in a browser here, and since fixed in the core, which now returns a plain
 * `ForbiddenError` for a non-plan 403 and never guesses at billing without a feature to
 * name.
 *
 * Kept because it is still the right preference for a diagnostic surface: the server's own
 * words describe what happened, and `getErrorMessage` is the fallback for the cases where
 * a client-side rendering is genuinely better (a field-level validation list, say).
 */
function serverMessage(error: unknown): string | null {
  const detail = (error as { detail?: unknown } | null)?.detail;
  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return null;
}

export type FaultKind =
  /** The service is not configured to authenticate. Re-authenticating cannot fix it. */
  | 'service_unconfigured'
  /** The credential is bad or expired. Go to the issuer. */
  | 'credential'
  /** The credential is fine and carries no organization this service can serve (403). */
  | 'tenant_unbound'
  /** The issuer is unreachable, or the canonical store is. Retry; do not sign out. */
  | 'unavailable'
  /** Nothing answered at all. */
  | 'unreachable'
  /** The plan does not include it. Not an authentication problem. */
  | 'not_in_plan'
  | 'other';

export interface Fault {
  kind: FaultKind;
  /** The server's own message where there is one. Never raw JSON. */
  message: string;
  code: string | null;
  status: number | null;
}

function statusOf(error: unknown): number | null {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

/**
 * Classify a failed request.
 *
 * `tokenWasSent` is not guessed from the error. The transport attaches a token when
 * `getToken` returns one, and only the caller knows whether it did — and the whole
 * `service_unconfigured` discrimination turns on that fact.
 */
export function diagnose(error: unknown, tokenWasSent: boolean): Fault {
  const code = errorCode(error);
  const status = statusOf(error);
  const message = getErrorMessage(error);

  if (error instanceof NetworkError) {
    return {
      kind: 'unreachable',
      code: null,
      status: null,
      message:
        'The inventory service did not answer. It may be starting, or the API base URL ' +
        'may be pointing somewhere else.',
    };
  }

  if (error instanceof ServiceUnavailableError) {
    // Two different 503s arrive here and both mean "retry, do not re-authenticate":
    // `identity_provider_unavailable` from core/api/auth.py, and a remote-mode store that
    // could not reach the canonical service.
    return {
      kind: 'unavailable',
      code,
      status,
      message:
        code === 'identity_provider_unavailable'
          ? 'The identity provider is unreachable, so the service cannot decide whether ' +
            'your token is valid. This is a 503 and not a 401 on purpose — your session ' +
            'is untouched and the shell is retrying.'
          : message,
    };
  }

  if (error instanceof UnauthorizedError) {
    if (tokenWasSent && (code === 'unauthenticated' || (!code && status === 401))) {
      return {
        kind: 'service_unconfigured',
        code,
        status,
        message:
          'The service accepted the request without ever inspecting the token, then ' +
          'refused it for having no verified claims. That is what an inventory service ' +
          'with no authentication configured does: it logs `auth.not_configured` at ' +
          'start-up and refuses every tenanted route rather than failing open.',
      };
    }
    return { kind: 'credential', code, status, message };
  }

  if (error instanceof FeatureNotInPlanError) {
    // The 403 is overloaded: core/api/deps.py raises it both for a missing tenant claim
    // and for a feature the plan does not include, and the client maps every 403 onto
    // FeatureNotInPlanError. The code is what tells them apart.
    if (code === 'tenant_claim_missing' || code === 'tenant_claim_unusable') {
      return { kind: 'tenant_unbound', code, status, message: serverMessage(error) ?? message };
    }
    return { kind: 'not_in_plan', code, status, message };
  }

  return { kind: 'other', code, status, message };
}

/** Whether this fault should send the user back to the issuer. Exactly one kind does. */
export function callsForReauthentication(fault: Fault): boolean {
  return fault.kind === 'credential';
}
